import React, { useCallback, useEffect, useState } from "react";
import jsPDF from "jspdf";
import { API, authHeaders } from "../../services/config";
import EmptyState from "../ui/EmptyState";
import "../PrestacionesPagos.css";
import "./RPPaciente.css";

const TIPOS = [
  { value: "rx", label: "Radiografías (RX)", icon: "📷" },
  { value: "medicamentos", label: "Medicamentos", icon: "💊" },
  { value: "certificados", label: "Certificados", icon: "📄" },
];

const tipoLabel = (tipo) => TIPOS.find((t) => t.value === tipo)?.label || tipo;

const formatFecha = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("es-AR", { year: "numeric", month: "long", day: "numeric" });
};

export default function RPPaciente({
  pacienteId,
  pacienteNombre = "",
  pacienteDni = "",
  embedded = false,
  onSaved,
}) {
  const [prescripciones, setPrescripciones] = useState([]);
  const [plantillas, setPlantillas] = useState([]);
  const [configClinica, setConfigClinica] = useState({
    membrete_modo: "completo",
    membrete_data_url: null,
    membrete_logo_data_url: null,
    membrete_texto: "",
    pie_modo: "completo",
    firma_sello_data_url: null,
    pie_firma_data_url: null,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({
    tipo: "rx",
    contenido: "",
    nombre: "",
    plantillaId: "",
  });

  const fetchPrescripciones = useCallback(async () => {
    const res = await fetch(`${API}/pacientes/${pacienteId}/prescripciones`, { headers: authHeaders() });
    const json = await res.json();
    if (res.ok && json.success) setPrescripciones(json.data || []);
  }, [pacienteId]);

  const fetchPlantillas = useCallback(async (tipo) => {
    const res = await fetch(`${API}/prescripciones/plantillas?tipo=${tipo}`, { headers: authHeaders() });
    const json = await res.json();
    if (res.ok && json.success) setPlantillas(json.data || []);
    else setPlantillas([]);
  }, []);

  const fetchConfig = useCallback(async () => {
    const res = await fetch(`${API}/configuracion-clinica`, { headers: authHeaders() });
    const json = await res.json();
    if (res.ok && json.success) {
      const data = {
        membrete_modo: json.data?.membrete_modo || "completo",
        membrete_data_url: json.data?.membrete_data_url || null,
        membrete_logo_data_url: json.data?.membrete_logo_data_url || null,
        membrete_texto: json.data?.membrete_texto || "",
        pie_modo: json.data?.pie_modo || "completo",
        firma_sello_data_url: json.data?.firma_sello_data_url || null,
        pie_firma_data_url: json.data?.pie_firma_data_url || null,
      };
      setConfigClinica(data);
      return data;
    }
    return null;
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        await Promise.all([fetchPrescripciones(), fetchConfig()]);
      } catch {
        setError("No se pudieron cargar las prescripciones.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [fetchPrescripciones, fetchConfig]);

  useEffect(() => {
    if (modalOpen) fetchPlantillas(form.tipo);
  }, [modalOpen, form.tipo, fetchPlantillas]);

  useEffect(() => {
    if (!modalOpen) return undefined;
    document.body.classList.add("pp-subview-modal-open");
    return () => document.body.classList.remove("pp-subview-modal-open");
  }, [modalOpen]);

  const openModal = () => {
    setForm({ tipo: "rx", contenido: "", nombre: "", plantillaId: "" });
    setError(null);
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const handlePlantillaSelect = (e) => {
    const id = e.target.value;
    const plantilla = plantillas.find((p) => String(p.id) === id);
    setForm((prev) => ({
      ...prev,
      plantillaId: id,
      contenido: plantilla ? plantilla.contenido : prev.contenido,
    }));
  };

  const handleGuardar = async () => {
    if (!form.contenido.trim()) {
      setError("El contenido es obligatorio.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API}/pacientes/${pacienteId}/prescripciones`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          tipo: form.tipo,
          contenido: form.contenido.trim(),
          nombre: form.nombre.trim() || null,
        }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        setError(json.message || "Error al guardar.");
        return;
      }
      setSuccess(form.nombre.trim() ? "Prescripción guardada como plantilla reutilizable." : "Prescripción guardada.");
      setTimeout(() => setSuccess(null), 3000);
      closeModal();
      await fetchPrescripciones();
      if (onSaved) onSaved();
    } catch {
      setError("Error de conexión al guardar.");
    } finally {
      setSaving(false);
    }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm("¿Eliminar esta prescripción?")) return;
    const res = await fetch(`${API}/prescripciones/${id}`, { method: "DELETE", headers: authHeaders() });
    if (res.ok) {
      setPrescripciones((prev) => prev.filter((p) => p.id !== id));
      if (onSaved) onSaved();
    }
  };

  const imprimirPDF = async (presc) => {
    try {
      const configData = await fetchConfig();
      const membreteModo = configData?.membrete_modo || configClinica.membrete_modo || "completo";
      const membreteData = configData?.membrete_data_url || configClinica.membrete_data_url;
      const membreteLogoData = configData?.membrete_logo_data_url || configClinica.membrete_logo_data_url;
      const membreteTexto = configData?.membrete_texto ?? configClinica.membrete_texto ?? "";
      const pieModo = configData?.pie_modo || configClinica.pie_modo || "completo";
      const firmaData = configData?.firma_sello_data_url || configClinica.firma_sello_data_url;
      const pieFirmaData = configData?.pie_firma_data_url || configClinica.pie_firma_data_url;

      const pdf = new jsPDF("p", "mm", "a4");
      const pageW = 210;
      const margin = 15;
      let y = 10;

      if (membreteModo === "armado") {
        if (membreteLogoData) {
          const logoFormat = membreteLogoData.includes("image/jpeg") ? "JPEG" : "PNG";
          pdf.addImage(membreteLogoData, logoFormat, pageW / 2 - 18, y, 36, 18);
          y += 22;
        }

        if (membreteTexto.trim()) {
          pdf.setFontSize(9);
          pdf.setFont("helvetica", "normal");
          const textLines = pdf.splitTextToSize(membreteTexto.trim(), pageW - margin * 2);
          textLines.forEach((line) => {
            pdf.text(line, pageW / 2, y, { align: "center" });
            y += 5;
          });
          y += 2;
        }

        pdf.setDrawColor(180, 180, 180);
        pdf.line(margin, y, pageW - margin, y);
        y += 10;
      } else if (membreteData) {
        const format = membreteData.includes("image/jpeg") ? "JPEG" : "PNG";
        pdf.addImage(membreteData, format, margin, y, pageW - margin * 2, 28);
        y += 32;
      }

      pdf.setFontSize(14);
      pdf.setFont("helvetica", "bold");
      pdf.text(tipoLabel(presc.tipo), margin, y);
      y += 8;

      pdf.setFontSize(10);
      pdf.setFont("helvetica", "normal");
      if (pacienteNombre) {
        pdf.text(`Paciente: ${pacienteNombre}`, margin, y);
        y += 6;
      }
      if (pacienteDni) {
        pdf.text(`DNI: ${pacienteDni}`, margin, y);
        y += 6;
      }
      pdf.text(`Fecha: ${formatFecha(presc.created_at)}`, margin, y);
      y += 10;

      pdf.setFontSize(11);
      const lines = pdf.splitTextToSize(presc.contenido, pageW - margin * 2);
      pdf.text(lines, margin, y);
      y += lines.length * 5 + 10;

      const pieY = 258;
      if (pieModo === "armado") {
        pdf.setDrawColor(180, 180, 180);
        pdf.line(margin, pieY, pageW - margin, pieY);
        if (pieFirmaData) {
          const pieFormat = pieFirmaData.includes("image/jpeg") ? "JPEG" : "PNG";
          pdf.addImage(pieFirmaData, pieFormat, pageW / 2 - 30, pieY + 4, 60, 22);
        }
      } else if (firmaData) {
        const firmaFormat = firmaData.includes("image/jpeg") ? "JPEG" : "PNG";
        pdf.addImage(firmaData, firmaFormat, margin, pieY - 2, pageW - margin * 2, 24);
      } else {
        pdf.setFontSize(10);
        pdf.text("___________________________", margin, 270);
        pdf.text("Firma y sello", margin, 276);
      }

      pdf.save(`prescripcion_${presc.tipo}_${presc.id}.pdf`);
    } catch (err) {
      console.error(err);
      setError("No se pudo generar el PDF.");
    }
  };

  return (
    <div className={`rp-paciente ${embedded ? "embedded" : ""}`}>
      <div className="rp-paciente-header">
        <h3>Prescripciones</h3>
        <p>Radiografías, medicamentos y certificados del paciente</p>
      </div>

      {error && <div className="rp-paciente-alert error">{error}</div>}
      {success && <div className="rp-paciente-alert success">{success}</div>}

      <button type="button" className="pp-btn-agregar rp-paciente-btn-new" onClick={openModal}>
        + Nueva prescripción
      </button>

      {loading ? (
        <p className="rp-paciente-loading">Cargando...</p>
      ) : prescripciones.length === 0 ? (
        <EmptyState title="Sin prescripciones" description="Todavía no hay prescripciones para este paciente." />
      ) : (
        <ul className="rp-paciente-lista">
          {prescripciones.map((p) => (
            <li key={p.id} className="rp-paciente-item pp-card">
              <div className="rp-paciente-item-top">
                <span className={`rp-paciente-badge tipo-${p.tipo}`}>{tipoLabel(p.tipo)}</span>
                {p.nombre ? <span className="rp-paciente-plantilla-tag">Plantilla: {p.nombre}</span> : null}
              </div>
              <p className="rp-paciente-fecha">{formatFecha(p.created_at)}</p>
              <p className="rp-paciente-preview">{p.contenido.slice(0, 120)}{p.contenido.length > 120 ? "…" : ""}</p>
              <div className="rp-paciente-acciones">
                <button type="button" className="boton-secundario" onClick={() => imprimirPDF(p)}>
                  Imprimir PDF
                </button>
                <button type="button" className="pp-btn-cerrar" onClick={() => handleEliminar(p.id)}>
                  Eliminar
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {modalOpen && (
        <div className="modal-historia pp-prest-nueva-overlay" role="presentation" onClick={closeModal}>
          <div
            className="modal-historia-content pp-prest-nueva-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="rp-nueva-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="rp-nueva-title">Nueva prescripción</h2>
            <div className="pp-prest-nueva-grid">
              <div className="pp-field pp-field--full">
                <span className="rp-paciente-label">Tipo</span>
                <div className="rp-paciente-tipos">
                  {TIPOS.map((t) => (
                    <button
                      key={t.value}
                      type="button"
                      className={`rp-paciente-tipo-btn ${form.tipo === t.value ? "active" : ""}`}
                      onClick={() => setForm((prev) => ({ ...prev, tipo: t.value, plantillaId: "", contenido: "" }))}
                    >
                      {t.icon} {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {plantillas.length > 0 && (
                <div className="pp-field pp-field--full">
                  <label htmlFor="rp-plantilla-select">Cargar desde plantilla</label>
                  <select
                    id="rp-plantilla-select"
                    className="pp-busqueda-input rp-paciente-select"
                    value={form.plantillaId}
                    onChange={handlePlantillaSelect}
                  >
                    <option value="">— Sin plantilla —</option>
                    {plantillas.map((pl) => (
                      <option key={pl.id} value={pl.id}>{pl.nombre}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="pp-field pp-field--full">
                <label htmlFor="rp-contenido">Contenido *</label>
                <textarea
                  id="rp-contenido"
                  className="rp-paciente-textarea"
                  rows={5}
                  value={form.contenido}
                  onChange={(e) => setForm((prev) => ({ ...prev, contenido: e.target.value }))}
                  placeholder="Escriba el pedido o modifique la plantilla seleccionada..."
                />
              </div>

              <div className="pp-field pp-field--full">
                <label htmlFor="rp-plantilla-nombre">Nombre de plantilla (opcional)</label>
                <input
                  id="rp-plantilla-nombre"
                  type="text"
                  className="pp-busqueda-input"
                  value={form.nombre}
                  onChange={(e) => setForm((prev) => ({ ...prev, nombre: e.target.value }))}
                  placeholder="Si completa este campo, podrá reutilizarla después"
                />
                <p className="rp-paciente-hint">
                  Sin nombre: se guarda solo para este paciente. Con nombre: queda disponible como plantilla en futuras prescripciones.
                </p>
              </div>

              {error && modalOpen ? <div className="rp-paciente-alert error pp-field--full">{error}</div> : null}
            </div>
            <div className="modal-historia-buttons">
              <button type="button" className="btn-cerrar" onClick={closeModal}>
                Cancelar
              </button>
              <button type="button" className="btn-guardar" onClick={handleGuardar} disabled={saving}>
                {saving ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
